import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, InputNumber, Form } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';

const Fees = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [formApi, setFormApi] = useState(null);

    const statusMap = {
        0: { text: '正常', type: 'success' },
        1: { text: '下架', type: 'danger' }
    };

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/shop/list_shop_fee/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/shop/add_shop_fee/', {
                ...values,
                price: Math.round(values.price * 100) // 转换为分
            });
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/shop/edit_shop_fee/', {
                ...values,
                uid: currentRecord.uid,
                price: Math.round(values.price * 100) // 转换为分
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/shop/delete_shop_fee/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的费用');
            return;
        }
        try {
            const response = await request.post('/go/sp/shop/batchedit_shop_fee/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            console.error('操作失败:', error);
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '费用名称',
            dataIndex: 'name',
            width: 200
        },
        {
            title: '价格',
            dataIndex: 'price',
            width: 150,
            render: (text) => `¥${(text / 100).toFixed(2)}`
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该费用吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const renderForm = (type = 'add') => {
        const isEdit = type === 'edit';
        const initialValues = isEdit && currentRecord ? {
            ...currentRecord,
            price: (currentRecord.price || 0) / 100 // 转换为元
        } : {
            name: '',
            price: 0,
            status: 0,
            rule: '{"fix":true}'
        };

        return (
            <Form
                getFormApi={setFormApi}
                initValues={initialValues}
                onSubmit={values => {
                    if (isEdit) {
                        handleEdit(values);
                    } else {
                        handleAdd(values);
                    }
                }}
                style={{ padding: '20px' }}
            >
                <Form.Input
                    field="name"
                    label="费用名称"
                    rules={[{ required: true, message: '请输入费用名称' }]}
                    placeholder="如：打包费、配送费"
                />
                <Form.InputNumber
                    field="price"
                    label="价格(元)"
                    rules={[{ required: true, message: '请输入价格' }]}
                    min={0}
                    precision={2}
                />
                <Form.Select
                    field="status"
                    label="状态"
                    initValue={0}
                >
                    <Select.Option value={0}>正常</Select.Option>
                    <Select.Option value={1}>下架</Select.Option>
                </Form.Select>
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space>
                        <Button type='tertiary' onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>取消</Button>
                        <Button type='primary' htmlType="submit">确定</Button>
                    </Space>
                </div>
            </Form>
        );
    };

    return (
        <div className={styles.feesContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索费用名称"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="费用状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增费用</Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button type='warning' onClick={() => handleBatchEdit(1, selectedRowKeys)}>批量下架</Button>
                            <Button type='primary' onClick={() => handleBatchEdit(0, selectedRowKeys)}>批量上架</Button>
                            <Popconfirm
                                title={`确定要删除选中的 ${selectedRowKeys.length} 个费用吗？`}
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title="新增费用"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
                width={700}
            >
                {renderForm('add')}
            </Modal>

            <Modal
                title="编辑费用"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
                width={700}
            >
                {renderForm('edit')}
            </Modal>
        </div>
    );
};

export default Fees;